import { Group, Modal, Stack } from "@mantine/core"
import { Badge, Button, Loader } from "@tfg/ui"
import { AxiosError } from "axios"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  useConfirmBulkUpdate,
  type ValidateBulkUpdateResponseDto,
} from "~/gen/api"
import { BulkUpdateErrorsSummary } from "~/pages/Brand/DataTransfer/Import/components/BulkUpdateErrorsSummary"
import { BulkUpdateWarningsSummary } from "~/pages/Brand/DataTransfer/Import/components/BulkUpdateWarningsSummary"
import { ProductBulkUpdateSummary } from "~/pages/Brand/DataTransfer/Import/components/ProductBulkUpdateSummary"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface BulkUpdateProductsModalProps {
  opened: boolean
  onClose: () => void
  response: ValidateBulkUpdateResponseDto | null
  isPending: boolean
}

export const BulkUpdateProductsModal = ({
  opened,
  onClose,
  response,
  isPending,
}: BulkUpdateProductsModalProps) => {
  const { t } = useTranslation("dataTransfer")
  const { user } = useAuth()
  const { mutate: confirmUpdate, isPending: isConfirming } =
    useConfirmBulkUpdate({
      mutation: {
        onSuccess: () => {
          showSuccessNotification({
            title: t("import.bulkUpdate.summaryModal.successTitle"),
            message: t("import.bulkUpdate.summaryModal.successMessage"),
          })
          onClose()
        },
        onError: (error: AxiosError) => {
          showAxiosError({
            notificationTitle: t("import.bulkUpdate.summaryModal.errorTitle"),
            notificationMessage: t(
              "import.bulkUpdate.summaryModal.errorMessage"
            ),
            showNotification: true,
            error,
          })
        },
      },
    })
  const summaryNode = useMemo(() => {
    if (!response) {
      return null
    }

    return (
      <Stack>
        <Group>
          <Badge
            variant="info"
            size="xs"
            text={t("import.bulkUpdate.summaryModal.totalRows", {
              count: response.summary.totalRows,
            })}
          />
          <Badge
            variant="success"
            size="xs"
            fz="xs"
            text={t("import.bulkUpdate.summaryModal.validRows", {
              count: response.summary.validRows,
            })}
          />
          <Badge
            variant="error"
            size="xs"
            fz="xs"
            text={t("import.bulkUpdate.summaryModal.erroredRows", {
              count: response.summary.erroredRows,
            })}
          />
          <Badge
            variant="warning"
            size="xs"
            fz="xs"
            text={t("import.bulkUpdate.summaryModal.warnedRows", {
              count: response.summary.warnedRows,
            })}
          />
        </Group>
        <Group>
          <ProductBulkUpdateSummary
            updatesPreview={response.details.updatesPreview}
          />
        </Group>
        <BulkUpdateErrorsSummary
          validationErrors={response.details.validationErrors}
        />
        <BulkUpdateWarningsSummary
          validationWarnings={response.details.validationWarnings}
        />
      </Stack>
    )
  }, [response, t])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("import.bulkUpdate.summaryModal.title")}
      centered
      size="xl"
    >
      <Stack gap="md" h="100%" justify="space-between">
        {isPending && (
          <Group h="100%" justify="center" align="center">
            <Loader fullPage />
          </Group>
        )}
        {summaryNode}
        {response &&
          response.details.validationErrors.length === 0 &&
          response.details.updatesPreview.length > 0 && (
            <Button
              variant="primary"
              onClick={() =>
                confirmUpdate({
                  data: {
                    organizationId: user?.organization?.id ?? "",
                  },
                })
              }
              disabled={isConfirming}
            >
              {t("import.bulkUpdate.summaryModal.confirmButton.label", {
                count: response.summary.validRows,
              })}
            </Button>
          )}
      </Stack>
    </Modal>
  )
}
